import { useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { GoogleLogo } from '@phosphor-icons/react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import Logo from '../shared/components/Logo';
import SignupModal, { type LoginData, type SignupData } from './SignupModal';
import introMain from '../../images/intromain.png';
import MainURL from '../../MainURL';
import {
  drumActivePlanAtom,
  drumLanguageAtom,
  drumLoggedInAtom,
  drumSelectedSongAtom,
  drumUserPreferencesAtom,
  drumUserProfileAtom,
  drumUserTokenAtom,
  type DrumUserPreferences,
  type DrumUserProfile,
} from '../shared/store/drumAppAtoms';
import { fetchMemberSession } from '../shared/api/memberApi';
import { applyMemberSessionData } from '../shared/hooks/useMemberSession';
import '../shared/styles/DrumApp.scss';
import './IntroLogin.scss';

// 구글 OAuth 웹 클라이언트 ID 가 있으면 실제 구글 로그인, 없으면 임시 이메일 로그인 모달을 쓴다.
const GOOGLE_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

function IntroLogin() {
  const loggedIn = useAtomValue(drumLoggedInAtom);
  const token = useAtomValue(drumUserTokenAtom);
  const setLoggedIn = useSetAtom(drumLoggedInAtom);
  const setSelectedSong = useSetAtom(drumSelectedSongAtom);
  const setUserToken = useSetAtom(drumUserTokenAtom);
  const setUserProfile = useSetAtom(drumUserProfileAtom);
  const setPreferences = useSetAtom(drumUserPreferencesAtom);
  const setLanguage = useSetAtom(drumLanguageAtom);
  const setActivePlan = useSetAtom(drumActivePlanAtom);
  const navigate = useNavigate();
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isAutoLogging, setIsAutoLogging] = useState(false);
  const [loginError, setLoginError] = useState('');

  const hasSavedSession = Boolean(loggedIn && token);
  const sessionSetters = { setUserProfile, setPreferences, setLanguage, setActivePlan };

  const enterApp = (data?: { token?: string; user?: DrumUserProfile; preferences?: DrumUserPreferences }) => {
    if (data?.token) setUserToken(data.token);
    applyMemberSessionData(data || {}, sessionSetters);
    setSelectedSong(null);
    setLoggedIn(true);
    navigate('/drum/library');
  };

  const clearSavedSession = () => {
    setLoggedIn(false);
    setUserToken('');
    setUserProfile(null);
  };

  const handleSignupClose = () => setIsSignupOpen(false);

  const handleLoginPress = async () => {
    if (isAutoLogging) return;
    if (!hasSavedSession) {
      setIsSignupOpen(true);
      return;
    }

    setIsAutoLogging(true);
    try {
      const session = await fetchMemberSession(token);
      enterApp({ token, ...session });
    } catch {
      clearSavedSession();
      if (GOOGLE_ENABLED) setLoginError('로그인이 만료되었습니다. 구글로 다시 로그인해 주세요.');
      else setIsSignupOpen(true);
    } finally {
      setIsAutoLogging(false);
    }
  };

  const postAuth = async (path: 'login' | 'signup' | 'google', body: LoginData | SignupData | { credential: string }, fallbackMessage: string) => {
    let response: Response;
    try {
      response = await fetch(`${MainURL}/auth/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      throw new Error(fallbackMessage);
    }

    const responseData = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(responseData?.message || fallbackMessage);
    }

    enterApp(responseData);
  };

  const handleLoginSubmit = (data: LoginData) => (
    postAuth('login', data, '로그인에 실패했습니다.')
  );

  const handleSignupSubmit = (data: SignupData) => (
    postAuth('signup', data, '회원가입에 실패했습니다.')
  );

  // 구글이 준 ID 토큰을 서버(/auth/google)에서 검증 → 같은 이메일 회원으로 로그인, 없으면 회원가입
  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      setLoginError('구글 로그인에 실패했습니다. 다시 시도해 주세요.');
      return;
    }
    setLoginError('');
    try {
      await postAuth('google', { credential: response.credential }, '구글 로그인에 실패했습니다.');
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : '구글 로그인에 실패했습니다.');
    }
  };

  return (
    <div className="drum-app" data-localized-root>
      <main className="dh-login-screen">
        <div className="login-art"><img src={introMain} alt="Outdoor rhythm stage" /></div>
        <section className="login-panel">
          <Logo />
          <h1>Rhythm that moves you.</h1>
          {GOOGLE_ENABLED && !hasSavedSession ? (
            <div className="google-login-wrap">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setLoginError('구글 로그인에 실패했습니다. 다시 시도해 주세요.')}
                text="continue_with"
                shape="pill"
                size="large"
              />
            </div>
          ) : (
            <button className="google-button" type="button" onClick={handleLoginPress} disabled={isAutoLogging}>
              <GoogleLogo weight="bold" /> {isAutoLogging ? '로그인 중...' : 'Continue with Google'}
            </button>
          )}
          {loginError && <p className="login-error" role="alert">{loginError}</p>}
          <button className="google-button admin-button" type="button" onClick={() => navigate('/admin')}>관리자페이지</button>
          <p className="login-consent">
            계속하면 <a href="/uploads/usingpolicy.html" target="_blank" rel="noopener noreferrer">이용약관</a> 및{' '}
            <a href="/uploads/personalinfo.html" target="_blank" rel="noopener noreferrer">개인정보처리방침</a>에 동의하게 됩니다.
          </p>
          <footer>Copyright © JUEUNEDUTECH. All rights reserved.</footer>
        </section>
      </main>
      {isSignupOpen && (
        <SignupModal onClose={handleSignupClose} onLogin={handleLoginSubmit} onSignup={handleSignupSubmit} />
      )}
    </div>
  );
}

export default IntroLogin;
